import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Form,
    Input,
    Button,
    Card,
    Row,
    Col,
    Select,
    Upload,
    message,
} from "antd";
import { PlusOutlined, SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import http from "../../helpers/http";
import HtmlEditor from "../../components/HtmlEditor";

const { TextArea } = Input;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/journals`;
const image_url = `${API_URL}uploads/images/`;

function BlogCreate() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [heroImgList, setHeroImgList] = useState([]);
    const [imgList, setImgList] = useState([]);
    const [detailImgList, setDetailImgList] = useState([]);

    const handleSubmit = async () => {
        try {
            const formValues = await form.validateFields();

            if (!formValues.title?.trim()) {
                message.error("Blog title is required");
                return;
            }

            setSubmitLoading(true);
            const formData = new FormData();

            formData.append("title", formValues.title.trim());
            formData.append("subTitle", formValues.subTitle || "");
            formData.append("desc", formValues.desc || "");
            formData.append("tag", formValues.tag || formValues.subTitle || "");
            formData.append("description", formValues.description || "");
            formData.append("description2", formValues.description2 || "");
            formData.append("status", formValues.status || "active");
            if (formValues.slug?.trim()) {
                formData.append("slug", formValues.slug.trim());
            }

            const tags = form.getFieldValue("tags");
            formData.append(
                "tags",
                Array.isArray(tags) && tags.length > 0 ? tags.join(",") : ""
            );

            if (heroImgList[0]?.originFileObj) {
                formData.append("heroImg", heroImgList[0].originFileObj);
            }
            if (imgList[0]?.originFileObj) {
                formData.append("img", imgList[0].originFileObj);
            }
            if (detailImgList[0]?.originFileObj) {
                formData.append("detailImg", detailImgList[0].originFileObj);
            }

            await http.post(base_url, formData);
            message.success("Blog post created successfully!");
            navigate("/blogs");
        } catch (error) {
            if (error.errorFields) return;
            console.error("Submit error:", error);
            message.error(error?.message || "Failed to create blog post");
        } finally {
            setSubmitLoading(false);
        }
    };

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    return (
        <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
            <div style={{ marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate("/blogs")}
                    style={{ marginBottom: 16 }}
                >
                    Back to Blogs
                </Button>
                <h1 style={{ margin: 0 }}>Create Blog Post</h1>
            </div>

            <Form
                form={form}
                layout="vertical"
                initialValues={{ status: "active" }}
            >
                <Card title="Basic Info" style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item
                                label="Title"
                                name="title"
                                rules={[{ required: true, message: "Please enter blog title" }]}
                            >
                                <Input size="large" placeholder="Blog title" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Subtitle (e.g. PEOPLE, PLACES, PERSPECTIVE)"
                                name="subTitle"
                            >
                                <Input placeholder="Subtitle" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Main tag" name="tag">
                                <Input placeholder="e.g. Design, News" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Additional tags" name="tags">
                                <Select
                                    mode="tags"
                                    style={{ width: "100%" }}
                                    placeholder="Add tags and press Enter"
                                    tokenSeparators={[","]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="URL Slug (optional)" name="slug" help="Leave blank to auto-generate">
                                <Input placeholder="e.g., my-first-post" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                label="Short excerpt (for listing)"
                                name="desc"
                            >
                                <TextArea rows={3} placeholder="Short description for listing cards" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Status" name="status">
                                <Select>
                                    <Select.Option value="active">Active</Select.Option>
                                    <Select.Option value="inactive">Inactive</Select.Option>
                                    <Select.Option value="draft">Draft</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card title="Images" style={{ marginBottom: 24 }}>
                    <Row gutter={24}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Hero / Banner (listing page)">
                                <Upload
                                    listType="picture-card"
                                    fileList={heroImgList}
                                    onChange={({ fileList }) => setHeroImgList(fileList.slice(-1))}
                                    beforeUpload={() => false}
                                    accept="image/*"
                                >
                                    {heroImgList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Card image (main)">
                                <Upload
                                    listType="picture-card"
                                    fileList={imgList}
                                    onChange={({ fileList }) => setImgList(fileList.slice(-1))}
                                    beforeUpload={() => false}
                                    accept="image/*"
                                >
                                    {imgList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Detail page image">
                                <Upload
                                    listType="picture-card"
                                    fileList={detailImgList}
                                    onChange={({ fileList }) => setDetailImgList(fileList.slice(-1))}
                                    beforeUpload={() => false}
                                    accept="image/*"
                                >
                                    {detailImgList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card title="Content" style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item label="Description first half" name="description">
                                <HtmlEditor height={400} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item label="Description second half" name="description2">
                                <HtmlEditor height={400} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <div style={{ marginTop: 24 }}>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSubmit}
                        loading={submitLoading}
                        size="large"
                        style={{ marginRight: 8 }}
                    >
                        Create Blog Post
                    </Button>
                    <Button onClick={() => navigate("/blogs")}>Cancel</Button>
                </div>
            </Form>
        </div>
    );
}

export default BlogCreate;
